import { Injectable, isDevMode } from "@angular/core";
import { Observable } from 'rxjs';

import { Acumatica } from '../acumatica-field-decorator';
import { AcumaticaService } from "../acumatica.service";
import { AcumaticaRequestOptions } from './../acumatica.service';
import { AcumaticaEntity, AcumaticaEntityService } from "./acumatica-entity";

import { BinderSpecification, SampleBooksService } from 'src/app/acumatica/EntityServices/SampleBooks.service';

@Injectable({
    providedIn: 'root'
})
export class SalesRepCatalogService extends AcumaticaEntityService {
    constructor(
        protected acumatica: AcumaticaService,
        private samplebooks: SampleBooksService,
    ) {
        super(acumatica, 'SalesRepCatalog', SalesRepCatalogInquiry);
    }

    /** returns array of SalesRepCatalogItem for all products available through rep app */
    public getItems(options?: AcumaticaRequestOptions, ignoreDeviceCache: boolean = false): Observable<SalesRepCatalogItem[]> {
        return new Observable<SalesRepCatalogItem[]>(subscriber => {

            // get items from local storage
            let itemsJSON = localStorage.getItem('sales-rep-catalog-items');

            let itemsCache: any = null;
            if (itemsJSON != null) { // decode the item contents
                itemsCache = JSON.parse(itemsJSON);

                if (itemsCache.asof) {
                    let asof = Date.parse(itemsCache.asof);
                    let diffTime = (Date.now()) - asof;
                    let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    if (diffDays > 7) ignoreDeviceCache = true;
                } else ignoreDeviceCache = true;

                if (ignoreDeviceCache) {
                    localStorage.removeItem('sales-rep-catalog-items');
                    itemsCache = null;
                }
            }

            if (itemsCache != null) {
                // convert plain objects to typed instances
                itemsCache.items.forEach((item: any, idx: number) => {
                    itemsCache.items[idx] = new SalesRepCatalogItem(item);
                });

                subscriber.next(itemsCache.items);
                subscriber.complete();
                return;
            }

            // fetch the items from Acumatica
            options = {
                ...{
                    expand: 'SalesRepCatalogDetails',
                    loader: 'Getting Catalog...'
                },
                ...options
            };

            super.putEntity(new AcumaticaEntity(), options).subscribe(response => {
                // store the items to local storage
                localStorage.setItem('sales-rep-catalog-items', JSON.stringify({
                    asof: new Date(),
                    items: response.Details
                }));

                subscriber.next(response.Details);
                subscriber.complete();
            })
        });
    }

    /** returns a structured ISalesRepCatalog with products grouped */
    public getCatalog(items: SalesRepCatalogItem[]): Observable<ISalesRepCatalog>;
    public getCatalog(items: AcumaticaRequestOptions): Observable<ISalesRepCatalog>;
    getCatalog(arg1: AcumaticaRequestOptions | SalesRepCatalogItem[]): Observable<ISalesRepCatalog> {
        return new Observable<any>(subscriber => {
            // function that will take SalesRepCatalogItem array (from API)
            // and return the structured JSON catalog object
            let items2catalog = (items: SalesRepCatalogItem[]) => {
                this.buildCatalog(items).subscribe(subscriber);
            }

            // given list of items to provided directly
            if (arg1 instanceof Array) {
                items2catalog(arg1);
                return;
            }

            // fetch items from the server
            this.getItems(arg1).subscribe(items => {
                // process the items into the catalog
                items2catalog(items)
            });
        });
    }

    /*
    Memo Samples
        Item Class
            [Collated Set]	            CL
            [Tag Set]                   TAG
            ..each Memo IDs             50
    Sample Books / Binders
        (All Cards)                     CD
        ..each Sample Book
            [Full Sample Book]          BND
            [Empty Binder Shell]	    EBND
            .. each Card ID             CD 
    */
    private buildCatalog(items: SalesRepCatalogItem[]): Observable<ISalesRepCatalog> {
        return new Observable<ISalesRepCatalog>(subscriber => {

            this.samplebooks.GetAllBindersContents().subscribe(() => {
                // create a map of all the cards within all the binders to their corresponding containg binder
                let card2bookMap: { [key: string]: string } = {};
                Object.values(this.samplebooks.BinderShelf).forEach(sampleBook => {
                    sampleBook.StockComponents.forEach(component => {
                        card2bookMap[component.InventoryID!] = sampleBook.InventoryID!;
                    })
                })

                let catalog: ISalesRepCatalog = { Memos: {}, Books: {}, ItemSets: {} };
                catalog.Books['_allFullBinders'] = { Items: [] };
                catalog.Books['_allEmptyBinders'] = { Items: [] };
                catalog.Books['_allCards'] = { Items: [] };
                let cardsNotInBinders: string[] = [];

                let getMemoClass = (patternClass: string | null, classDescription: string | null): ISalesRepCatalogMemoClass => {
                    patternClass = patternClass || '(unknown)';
                    if (catalog.Memos[patternClass] === undefined) {
                        catalog.Memos[patternClass] = {
                            Description: classDescription || '',
                            SampleCard: [],
                            MemoItems: []
                        }
                    }
                    return catalog.Memos[patternClass];
                }

                let getSampleBook = (sampleBook: BinderSpecification): ISalesRepCatalogSampleBook => {
                    let binderID = sampleBook.InventoryID!;
                    if (catalog.Books[binderID!] === undefined) {
                        catalog.Books[binderID] = {
                            Items: []
                        }
                    }
                    return catalog.Books[binderID];
                }

                let memoClass: ISalesRepCatalogMemoClass;
                items
                    .sort((item1, item2) => item1.ItemDescription.localeCompare(item2.ItemDescription))
                    .forEach((item, idx) => {
                        // items with no 'Pattern Class' specified
                        if (item.PatternClass === null) return;

                        switch (item.ClassID.trim()) {
                            case '50':
                                // memo sample
                                memoClass = getMemoClass(item.PatternClass, item.PatternClassDescription);
                                memoClass.MemoItems.push(item);
                                break;
                            case 'CL':
                                // collated set
                                memoClass = getMemoClass(item.PatternClass, item.PatternClassDescription);
                                memoClass.CollatedSetItem = item;
                                break;
                            case 'TAG':
                                // tag set
                                memoClass = getMemoClass(item.PatternClass, item.PatternClassDescription);
                                memoClass.TagSetItem = item;
                                break;

                            case 'BND':
                                // full binder
                                let binder = this.samplebooks.BinderShelf[item.InventoryID];
                                if (binder !== undefined) {
                                    getSampleBook(binder).FullBinderItem = item;
                                } else {
                                    console.warn('Full Binder ID not defined in Sample Books service: ' + item.InventoryID);
                                }

                                catalog.Books['_allFullBinders'].Items.push(item);
                                break;
                            case 'EBND':
                                // empty binder
                                let binderShell = this.samplebooks.BinderShells.find(shell => shell.InventoryID == item.InventoryID);
                                if (binderShell !== undefined) {
                                    let binder = this.samplebooks.BinderShelf[binderShell.FullBinderID];
                                    if (binder === undefined) throw new Error("Full Binder ID \"" + binderShell.FullBinderID + "\" for Sample Book Shell \"" + item.InventoryID + "\" is not specified within the 'BinderShelf' collection");
                                    getSampleBook(binder).EmptyBinderItem = item;
                                } else {
                                    console.warn('Empty Binder Shell ID not defined in Sample Books service: ' + item.InventoryID);
                                }

                                catalog.Books['_allEmptyBinders'].Items.push(item);
                                break;
                            case 'CD':
                                // sample book class card
                                if (item.PatternClass) {
                                    getMemoClass(item.PatternClass, item.PatternClassDescription).SampleCard.push(item);
                                }

                                // get sample book ID from map
                                let binderID = card2bookMap[item.InventoryID];
                                if (binderID !== undefined) {
                                    let binder = this.samplebooks.BinderShelf[binderID];
                                    (catalog.ItemSets[binder.ItemsetGroup!] ||= []).push(item.InventoryID); // add the item to the 'itemsets' array for one-button 'add all (set type) cards'                                
                                    getSampleBook(binder).Items.push(item);
                                } else {
                                    cardsNotInBinders.push(item.InventoryID + " - " + item.ItemDescription);
                                }

                                catalog.Books['_allCards'].Items.push(item);
                                break;
                            default:
                                throw new Error("Unexpected Item Class in Catalog: '" + item.ClassID + "'");
                        }
                    });

                // if (cardsNotInBinders.length > 0) console.warn("Cards not in any Binder Kit Specifications\n" + cardsNotInBinders.join("\n"));
                catalog.Books['_allCards'].Items = catalog.Books['_allCards'].Items.sort((a, b) =>
                    a.ItemDescription.localeCompare(b.ItemDescription)
                );

                subscriber.next(catalog);
                subscriber.complete();
            })

        })
    }

    // create and return an object in flattened structure to be used with the Materials Tree control
    public catalogToTree(catalog: ISalesRepCatalog, sortMethod?: CatalogTreeSortMethod): ICatalogTreeNode[] {

        const items2nodes = (item: SalesRepCatalogItem) => {
            return {
                description: item.ItemDescription,
                inventoryID: item.InventoryID,
                ref: item,
                children: []
            } as ICatalogTreeNode;
        }

        const allowItemSets = false && isDevMode();

        const book2node = (nodeDescription: string, addAllButtonLabel: string, bookID: string) => {
            return {
                description: nodeDescription,
                children: [
                    // add all cards from book
                    ...allowItemSets ? [{
                        description: addAllButtonLabel,
                        itemset: catalog.Books[bookID].Items.map(item => item.InventoryID)
                    }] : [],

                    // each specific book card
                    ...catalog.Books[bookID].Items.map(items2nodes)
                ]
            }
        }

        let nodeMemoSamples: ICatalogTreeNode = { description: "Memo Samples", inventoryID: '', children: [] };
        let nodeSampleBooks: ICatalogTreeNode = {
            description: "Sample Books", children: [
                book2node("Full Sample Books", "All Sample Books", '_allFullBinders'),
                book2node("Empty Binder Shells", "All Empty binder shells", '_allEmptyBinders'),
                {
                    description: "All Sample Cards", children: [
                        ...allowItemSets ? [
                            {
                                description: 'All Upholstery Fabric Cards',
                                itemset: catalog.ItemSets['Upholstery']
                            },
                            {
                                description: 'All Coated Fabric Cards',
                                itemset: catalog.ItemSets['Coated']
                            },
                            {
                                description: 'All Privacy Curtains & Drapery Cards',
                                itemset: catalog.ItemSets['Privacy Curtains & Drapery']
                            },
                            {
                                description: 'All Panel Fabric Cards',
                                itemset: catalog.ItemSets['Panel']
                            },

                        ] : [],
                        ...catalog.Books['_allCards'].Items.map(items2nodes)
                    ]
                }
            ]
        };

        // Memo Sample Item Classes
        Object.keys(catalog.Memos).forEach(classID => {
            let productClass = catalog.Memos[classID];
            // Product Class parent level node ("Caressa - CAS")
            let nodeProductClass: ICatalogTreeNode = {
                key: 'CLASS/' + classID,
                description: productClass.Description + (productClass.Description ? ' - ' : '') + classID,
                inventoryID: '',
                ref: productClass,
                children: []
            };

            // Collated Set item (only 1)
            if (productClass.CollatedSetItem) {
                let nodeItem: ICatalogTreeNode = {
                    key: 'CS/' + productClass.CollatedSetItem.InventoryID,
                    description: productClass.CollatedSetItem.ItemDescription,
                    inventoryID: productClass.CollatedSetItem.InventoryID,
                    ref: productClass.CollatedSetItem
                };
                nodeProductClass.children?.push(nodeItem);
            }

            // Tag Set item (only 1)
            if (productClass.TagSetItem) {
                let nodeItem: ICatalogTreeNode = {
                    key: 'TS/' + productClass.TagSetItem.InventoryID,
                    description: productClass.TagSetItem.ItemDescription,
                    inventoryID: productClass.TagSetItem.InventoryID,
                    ref: productClass.TagSetItem
                };
                nodeProductClass.children?.push(nodeItem);
            }

            // Pattern sample card(s)
            productClass.SampleCard.forEach(sampleCard => {
                let nodeItem: ICatalogTreeNode = {
                    key: 'CD/' + sampleCard.InventoryID,
                    description: sampleCard.ItemDescription,
                    inventoryID: sampleCard.InventoryID,
                    ref: sampleCard
                };
                nodeProductClass.children?.push(nodeItem);
            });

            // Memo item(s)
            productClass.MemoItems
                .sort((a, b) => {
                    switch (sortMethod) {
                        case undefined:
                        case CatalogTreeSortMethod.alphabetical:
                            return a.ItemDescription.localeCompare(b.ItemDescription);
                        case CatalogTreeSortMethod.color:
                            return a.InventoryID.reverse().localeCompare(b.InventoryID.reverse());
                        case CatalogTreeSortMethod.sku:
                            return a.InventoryID.localeCompare(b.InventoryID);
                    }
                })
                .forEach(memoItem => {
                    let nodeItem: ICatalogTreeNode = {
                        key: 'MEMO/' + memoItem.InventoryID,
                        description: memoItem.ItemDescription,
                        inventoryID: memoItem.InventoryID,
                        ref: memoItem
                    };
                    nodeProductClass.children?.push(nodeItem);
                });

            nodeMemoSamples.children!.push(nodeProductClass);
        });

        // sort the Product Class parent level tree nodes by pattern name
        nodeMemoSamples.children = nodeMemoSamples.children?.sort((a, b) => {
            let aRef = a.ref as ISalesRepCatalogMemoClass;
            let bRef = b.ref as ISalesRepCatalogMemoClass;
            return aRef.Description!.localeCompare(bRef.Description!);
        });

        let books: ICatalogTreeNode[] = [];
        Object.keys(catalog.Books).forEach(sampleBookID => {
            if (sampleBookID.substring(0, 4) == '_all') return;

            let sampleBook = catalog.Books[sampleBookID];

            if (!sampleBook.FullBinderItem) return;

            let nodeSampleBook: ICatalogTreeNode = {
                description: sampleBook.FullBinderItem!.InventoryID + " - " + sampleBook.FullBinderItem!.ItemDescription,
                inventoryID: '',
                ref: sampleBook,
                children: []
            }

            // Decision made to not list Full Binders and Empty Shells under specific binder nodes
            /*
            // Full book
            if (sampleBook.FullBinderItem) {
                let nodeItem: ICatalogTreeNode = {
                    description: sampleBook.FullBinderItem.ItemDescription,
                    inventoryID: sampleBook.FullBinderItem.InventoryID,
                    ref: sampleBook.FullBinderItem
                };
                nodeSampleBook.children?.push(nodeItem);
            }

            // Empty shell
            if (sampleBook.EmptyBinderItem) {
                let nodeItem: ICatalogTreeNode = {
                    description: sampleBook.EmptyBinderItem.ItemDescription,
                    inventoryID: sampleBook.EmptyBinderItem.InventoryID,
                    ref: sampleBook.EmptyBinderItem
                };
                nodeSampleBook.children?.push(nodeItem);
            }
            */

            // Cards
            let cards: ICatalogTreeNode[] = [];
            sampleBook.Items.forEach(card => {
                let nodeItem: ICatalogTreeNode = {
                    description: card.ItemDescription,
                    inventoryID: card.InventoryID,
                    ref: card
                };
                cards.push(nodeItem);
            });

            // sort cards alphabetically and add after static items
            cards = cards.sort((a: ICatalogTreeNode, b: ICatalogTreeNode) => a.description.localeCompare(b.description));
            nodeSampleBook.children = nodeSampleBook.children!.concat(cards);

            books.push(nodeSampleBook);
        });
        books = books.sort((a, b) => a.description!.localeCompare(b.description!));
        nodeSampleBooks.children = nodeSampleBooks.children!.concat(books);

        return [nodeMemoSamples, nodeSampleBooks];
    }
}

export interface ICatalogTreeNode {
    key?: string;
    description: string;
    inventoryID?: string;
    itemset?: string[];
    children?: ICatalogTreeNode[];
    ref?: ISalesRepCatalogMemoClass | ISalesRepCatalogSampleBook | SalesRepCatalogItem;
}

interface ISalesRepCatalogMemoClass {
    Description: string | null;
    CollatedSetItem?: SalesRepCatalogItem,
    TagSetItem?: SalesRepCatalogItem,
    SampleCard: SalesRepCatalogItem[],
    MemoItems: SalesRepCatalogItem[],
}

interface ISalesRepCatalogSampleBook {
    FullBinderItem?: SalesRepCatalogItem,
    EmptyBinderItem?: SalesRepCatalogItem,
    Items: SalesRepCatalogItem[]
}

export interface ISalesRepCatalog {
    Memos: {
        [key: string]: ISalesRepCatalogMemoClass;
    },
    Books: {
        [key: string]: ISalesRepCatalogSampleBook;
    },
    ItemSets: {
        [key: string]: string[]
    },
}

export class SalesRepCatalogItem extends AcumaticaEntity {
    @Acumatica() ClassID!: string;
    @Acumatica() ClassDescription!: string;
    @Acumatica() InventoryID!: string;
    @Acumatica() ItemDescription!: string;
    @Acumatica() LifeCycle!: string | null;
    @Acumatica() PatternClass!: string | null;
    @Acumatica() PatternClassDescription!: string | null;
    @Acumatica() ItemStatus!: string;
}

export class SalesRepCatalogInquiry extends AcumaticaEntity {
    @Acumatica({ name: 'SalesRepCatalogDetails', type: SalesRepCatalogItem }) Details: SalesRepCatalogItem[] = [];
}

export enum CatalogTreeSortMethod {
    alphabetical,
    color,
    sku
}

export enum CatalogItemViewMode {
    list,
    grid
}